/**
 * Utility functions for finding product images based on variant selections
 */

/**
 * Find the appropriate image for a product based on selected variant options
 * Follows hierarchy: variant combination image > variant option value image > primary image > first image
 * @param product - Product object with images, variantOptions, and variantCombinations
 * @param selectedVariantOptions - Selected variant options (array or object format)
 * @returns The image object or undefined
 */
export const findProductImage = (
  product: any,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }> | Record<string, string>
): { url: string; isPrimary?: boolean } | undefined => {
  if (!product?.images || product.images.length === 0) {
    return undefined;
  }

  // Convert selectedVariantOptions to object format if it's an array
  let selectedOptions: Record<string, string> = {};
  if (Array.isArray(selectedVariantOptions)) {
    selectedOptions = selectedVariantOptions.reduce((acc, opt) => {
      acc[opt.optionName] = opt.optionValue;
      return acc;
    }, {} as Record<string, string>);
  } else if (selectedVariantOptions) {
    selectedOptions = selectedVariantOptions;
  }

  // Build variant combination string
  const buildVariantCombination = (options: Record<string, string>): string => {
    return Object.entries(options)
      .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
      .sort()
      .join("|");
  };

  const combination = buildVariantCombination(selectedOptions);

  // 1. Check variantCombinations array for imageIndex (highest priority)
  if (combination && product.variantCombinations) {
    const variantCombo = product.variantCombinations.find(
      (vc: any) => vc.combination === combination
    );
    if (variantCombo && variantCombo.imageIndex !== undefined && variantCombo.imageIndex >= 0) {
      const image = product.images[variantCombo.imageIndex];
      if (image) {
        return image;
      }
    }
  }

  // 2. Fallback to old structure: check images for variantCombinations
  if (combination) {
    const matchingImage = product.images.find((img: any) => 
      img.variantCombinations && img.variantCombinations.includes(combination)
    );
    if (matchingImage) {
      return matchingImage;
    }
  }

  // 3. Check for variant option value images
  if (Object.keys(selectedOptions).length > 0) {
    for (const option of product.variantOptions || []) {
      const selectedValue = selectedOptions[option.name];
      if (selectedValue) {
        const optionValue = option.values?.find((v: any) => v.value === selectedValue && v.isActive);
        if (optionValue?.image) {
          // Find image by URL
          const matchingImage = product.images.find((img: any) => 
            img.url === optionValue.image || img.originalUrl === optionValue.image
          );
          if (matchingImage) {
            return matchingImage;
          }
        }
      }
    }
  }

  // 4. Fallback to primary image or first image
  const primaryImage = product.images.find((img: any) => img.isPrimary);
  return primaryImage || product.images[0];
};

